import type { HoleBooleanMap, SluggedId } from "Models";
import {
  windowsFileNameXmlRegex,
  sluggedIdSchema,
  descriptionSchema,
  nameSchema,
  sluggedIdValidator,
  holeBooleanMapSchema,
} from "entities/common";
import { z } from "zod";
import { AdKitKey } from "types/enums";

type CreateAdKitLocationInstanceInternalDto = {
  imageId: SluggedId; // required integer
  overrideWarnings: boolean; // required boolean
  holeNumber: number; // required integer (0 means it doesn't show up on a specific hole; valid values 0–18)
  optionalImageFilePathOverride?: string | null; // optional string (null/undefined if not provided, max length 100)
  orderLocation?: number; // optional integer with default -1 if not provided (range: -1 to int.MaxValue)
  widthAdjustment?: number; // optional integer with default -1 (range: -1 to int.MaxValue)
  heightAdjustment?: number; // optional integer with default -1 (range: -1 to int.MaxValue)
};

type CreateAdKitAdLocationInternalDto = {
  adKitMetaDataId: AdKitKey;
  enabledHoles?: HoleBooleanMap;
  overrideWarnings: boolean;
  adInstances?: CreateAdKitLocationInstanceInternalDto[];
};

type CreateAdKitInternalDto = {
  name: string;
  description?: string | null;
  fileName?: string;
  transitionDisplay?: number;
  transitionFadeIn?: number;
  transitionFadeOut?: number;
  adLocations?: CreateAdKitAdLocationInternalDto[];
};

type UpdateAdKitAdLocationInternalDto = {
  adKitMetaDataId?: AdKitKey;
  enabledHoles?: HoleBooleanMap;
  overrideWarnings: boolean;
  restoreDeleted: boolean;
  adInstances?: UpdateAdLocationInstanceWithIdInternalDto[];
};

type UpdateAdLocationInstanceInternalDto = {
  imageId?: SluggedId;
  overrideWarnings: boolean;
  restoreDeleted: boolean;
  holeNumber?: number;
  optionalImageFilePathOverride?: string | null;
  orderLocation?: number;
  widthAdjustment?: number;
  heightAdjustment?: number;
};

type UpdateAdKitAdLocationWithIdInternalDto =
  UpdateAdKitAdLocationInternalDto & {
    id?: SluggedId;
  };

type UpdateAdLocationInstanceWithIdInternalDto =
  UpdateAdLocationInstanceInternalDto & {
    id?: SluggedId;
  };

type UpdateAdKitInternalDto = {
  name?: string;
  description?: string | null;
  fileName?: string;
  transitionDisplay?: number;
  transitionFadeIn?: number;
  transitionFadeOut?: number;
  restoreDeleted: boolean;
  adLocations?: UpdateAdKitAdLocationInternalDto[];
};

const CreateAdKitLocationInstanceDtoRawSchema = z.object({
  // Required and must be an integer
  imageId: sluggedIdValidator,
  // Required boolean
  overrideWarnings: z.boolean(),
  // Must be an integer between 0 and 18 (inclusive)
  holeNumber: z.number().int().min(0).max(18).default(0),
  // Optional string that may be null and, if provided, cannot exceed 100 characters
  optionalImageFilePathOverride: z
    .string()
    .max(100, {
      error: "optionalImageFilePathOverride must be 100 characters or fewer",
    })
    .regex(windowsFileNameXmlRegex, {
      error: "optionalImageFilePathOverride must be a valid Windows file name",
    })
    .optional()
    .nullable(),
  // Optional integer with a default value of -1 (must be >= -1)
  orderLocation: z.number().int().min(-1).default(-1),
  // Optional integer with a default value of -1 (must be >= -1)
  widthAdjustment: z.number().int().min(-1).default(-1),
  // Optional integer with a default value of -1 (must be >= -1)
  heightAdjustment: z.number().int().min(-1).default(-1),
});

export const CreateAdKitLocationDtoRawSchema = z.object({
  adKitMetaDataId: z.enum(AdKitKey),
  // enabledHoles is optional. If provided, it must adhere to the HoleBooleanMapSchema.
  enabledHoles: z.optional(holeBooleanMapSchema),
  overrideWarnings: z.boolean(),
  adInstances: z.optional(z.array(CreateAdKitLocationInstanceDtoRawSchema)),
});

export const CreateAdKitLocationInstanceDtoSchema =
  CreateAdKitLocationInstanceDtoRawSchema satisfies z.ZodType<CreateAdKitLocationInstanceInternalDto>;

export const CreateAdKitLocationDtoSchema =
  CreateAdKitLocationDtoRawSchema satisfies z.ZodType<CreateAdKitAdLocationInternalDto>;

export type CreateAdKitLocationInstanceDto = z.infer<
  typeof CreateAdKitLocationInstanceDtoSchema
>;
export type CreateAdKitLocationDto = z.infer<
  typeof CreateAdKitLocationDtoSchema
>;

export const CreateAdKitDtoRawSchema = z.object({
  ...nameSchema,
  ...descriptionSchema,
  fileName: z
    .string()
    .max(100, { error: "fileName must be 100 characters or fewer" })
    .regex(windowsFileNameXmlRegex, {
      error: "fileName must be a valid Windows file name and end in .xml",
    })
    // Optional per swagger: the API derives a safe file name from `name`.
    .optional(),

  transitionDisplay: z.number().optional(),
  transitionFadeIn: z.number().optional(),
  transitionFadeOut: z.number().optional(),
  adLocations: z.array(CreateAdKitLocationDtoSchema).optional(), //z.optional(z.array(CreateAdKitLocationDtoSchema)),
});

const UpdateAdLocationInstanceWithIdRawSchema = z
  .object({
    ...sluggedIdSchema,
    overrideWarnings: z.boolean(),
    restoreDeleted: z.boolean(),
    // Must be an integer between 0 and 18 (inclusive)
    holeNumber: z.number().int().min(0).max(18).optional(),
    // Optional string that may be null and, if provided, cannot exceed 100 characters
    optionalImageFilePathOverride: z
      .string()
      .max(100, {
        error: "optionalImageFilePathOverride must be 100 characters or fewer",
      })
      .regex(windowsFileNameXmlRegex, {
        error:
          "optionalImageFilePathOverride must be a valid Windows file name",
      })
      .optional()
      .nullable(),
    // Optional integer with a default value of -1 (must be >= -1)
    orderLocation: z.number().int().min(-1).optional(),
    // Optional integer with a default value of -1 (must be >= -1)
    widthAdjustment: z.number().int().min(-1).optional(),
    // Optional integer with a default value of -1 (must be >= -1)
    heightAdjustment: z.number().int().min(-1).optional(),
  })
  .extend({
    id: sluggedIdSchema.id.optional(), // overrides id to be optional
  });

const UpdateAdKitAdLocationRawSchema = z.object({
  adKitMetaDataId: z.enum(AdKitKey).optional(),
  // enabledHoles is optional. If provided, it must adhere to the HoleBooleanMapSchema.
  enabledHoles: z.optional(holeBooleanMapSchema),
  overrideWarnings: z.boolean(),
  restoreDeleted: z.boolean(),
  adInstances: z.array(UpdateAdLocationInstanceWithIdRawSchema).optional(), //z.optional(z.array(UpdateAdLocationInstanceWithIdRawSchema)),
});

const UpdateAdLocationInstanceRawSchema = z.object({
  imageId: sluggedIdValidator.optional(),
  overrideWarnings: z.boolean(),
  restoreDeleted: z.boolean(),
  // Must be an integer between 0 and 18 (inclusive)
  holeNumber: z.number().int().min(0).max(18).optional(),
  // Optional string that may be null and, if provided, cannot exceed 100 characters
  optionalImageFilePathOverride: z
    .string()
    .max(100, {
      error: "optionalImageFilePathOverride must be 100 characters or fewer",
    })
    .regex(windowsFileNameXmlRegex, {
      error: "optionalImageFilePathOverride must be a valid Windows file name",
    })
    .optional()
    .nullable(),
  // Optional integer with a default value of -1 (must be >= -1)
  orderLocation: z.number().int().min(-1).optional(),
  // Optional integer with a default value of -1 (must be >= -1)
  widthAdjustment: z.number().int().min(-1).optional(),
  // Optional integer with a default value of -1 (must be >= -1)
  heightAdjustment: z.number().int().min(-1).optional(),
});

const UpdateAdKitAdLocationWithIdRawSchema = z
  .object({
    ...sluggedIdSchema,
    adKitMetaDataId: z.enum(AdKitKey).optional(),
    // enabledHoles is optional. If provided, it must adhere to the HoleBooleanMapSchema.
    enabledHoles: z.optional(holeBooleanMapSchema),
    overrideWarnings: z.boolean(),
    restoreDeleted: z.boolean(),
  })
  .extend({
    id: sluggedIdSchema.id.optional(), // overrides id to be optional
  });

const UpdateAdKitDtoRawSchema = z
  .object({
    ...nameSchema,
    ...descriptionSchema,
    fileName: z
      .string()
      .max(100, { error: "fileName must be 100 characters or fewer" })
      .regex(windowsFileNameXmlRegex, {
        error: "fileName must be a valid Windows file name and end in .xml",
      })
      .optional(),

    transitionDisplay: z.number().optional(),
    transitionFadeIn: z.number().optional(),
    transitionFadeOut: z.number().optional(),
    restoreDeleted: z.boolean(),
    adLocations: z.array(UpdateAdKitAdLocationWithIdRawSchema).optional(), //z.optional(z.array(UpdateAdKitAdLocationWithIdRawSchema)),
  })
  .extend({
    name: nameSchema.name.optional(), // overrides name to be optional
  });

export const CreateAdKitDtoSchema =
  CreateAdKitDtoRawSchema satisfies z.ZodType<CreateAdKitInternalDto>;

export const UpdateAdKitDtoSchema =
  UpdateAdKitDtoRawSchema satisfies z.ZodType<UpdateAdKitInternalDto>;

export const UpdateAdKitAdLocationDtoSchema =
  UpdateAdKitAdLocationRawSchema satisfies z.ZodType<UpdateAdKitAdLocationInternalDto>;

export const UpdateAdKitAdLocationWithIdDtoSchema =
  UpdateAdKitAdLocationWithIdRawSchema satisfies z.ZodType<UpdateAdKitAdLocationWithIdInternalDto>;

export const UpdateAdLocationInstanceDtoSchema =
  UpdateAdLocationInstanceRawSchema satisfies z.ZodType<UpdateAdLocationInstanceInternalDto>;

export const UpdateAdLocationInstanceWithIdDtoSchema =
  UpdateAdLocationInstanceWithIdRawSchema satisfies z.ZodType<UpdateAdLocationInstanceWithIdInternalDto>;

export type CreateAdKitDto = z.infer<typeof CreateAdKitDtoSchema>;
export type UpdateAdKitDto = z.infer<typeof UpdateAdKitDtoSchema>;
export type UpdateAdKitLocationDto = z.infer<
  typeof UpdateAdKitAdLocationDtoSchema
>;
export type UpdateAdLocationInstanceDto = z.infer<
  typeof UpdateAdLocationInstanceDtoSchema
>;
export type UpdateAdKitLocationByIdDto = z.infer<
  typeof UpdateAdKitAdLocationWithIdDtoSchema
>;
export type UpdateAdLocationInstanceWithIdDto = z.infer<
  typeof UpdateAdLocationInstanceWithIdDtoSchema
>;
